import { useEffect, useState } from "react";
import { Loader2, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ALG } from "@/constants/testIds";
import { searchAllergens } from "@/lib/allergenApi";
import {
  ALLERGEN_TYPES,
  formatSearchAllergen,
  toSelectedAllergen,
} from "@/lib/allergyLookup";

function apiErrorMessage(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((row) => row.msg).join("; ");
  return "Unable to search allergens.";
}

function FieldLabel({ children }) {
  return <label className="mb-1 block text-xs font-medium text-slate-600">{children}</label>;
}

export default function AllergyLookup({
  onSelect,
  selected = null,
  allergenType = "",
  readOnly = false,
  autoFocus = true,
}) {
  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [typeFilter, setTypeFilter] = useState(allergenType || "");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [current, setCurrent] = useState(selected);

  useEffect(() => {
    setCurrent(selected);
  }, [selected]);

  useEffect(() => {
    const handle = setTimeout(() => setDebounced(query.trim()), 300);
    return () => clearTimeout(handle);
  }, [query]);

  useEffect(() => {
    if (current) return undefined;
    if (!debounced) {
      setItems([]);
      setError(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    searchAllergens({
      q: debounced,
      allergenType: typeFilter || undefined,
      active: true,
      limit: 25,
    })
      .then((data) => {
        if (cancelled) return;
        setItems(data.items || []);
      })
      .catch((err) => {
        if (cancelled) return;
        setItems([]);
        setError(apiErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debounced, typeFilter, current]);

  function selectItem(item) {
    const next = toSelectedAllergen(item);
    setCurrent(item);
    setQuery("");
    setItems([]);
    onSelect?.(next, item);
  }

  function changeAllergen() {
    setCurrent(null);
    onSelect?.(null, null);
  }

  if (current) {
    const label = current.allergenTypeLabel || current.allergenType;
    return (
      <div data-testid={ALG.lookup} className="space-y-3">
        <div data-testid={ALG.selected} className="rounded-md border border-[#e4d8c8] bg-[#FBF8FF] p-3">
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Selected Allergen
              </p>
              <p
                data-testid={ALG.selectedName}
                className="mt-0.5 text-base font-semibold text-slate-900"
              >
                {current.displayName}
              </p>
              <p
                data-testid={ALG.selectedType}
                className="mt-1 text-sm text-slate-600"
              >
                {label}
              </p>
            </div>
            {!readOnly && (
              <Button
                type="button"
                variant="outline"
                data-testid={ALG.change}
                onClick={changeAllergen}
                className="h-8 border-[#6B4C9A]/40 text-[#6B4C9A]"
              >
                Change Allergen
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid={ALG.lookup} className="space-y-3">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input
          type="search"
          name="allergy-lookup"
          autoComplete="off"
          spellCheck={false}
          data-testid={ALG.search}
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search allergens by name or synonym…"
          className="h-10 bg-white pl-10 pr-9"
          autoFocus={autoFocus}
          disabled={readOnly}
        />
        {query ? (
          <button
            type="button"
            className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            onClick={() => setQuery("")}
            aria-label="Clear search"
          >
            <X className="h-4 w-4" />
          </button>
        ) : null}
      </div>

      <div>
        <FieldLabel>Allergen type (optional)</FieldLabel>
        <select
          data-testid={ALG.typeFilter}
          className="h-9 w-full rounded-md border border-[#e4d8c8] bg-white px-2 text-sm text-slate-800"
          value={typeFilter}
          onChange={(event) => setTypeFilter(event.target.value)}
          disabled={readOnly}
        >
          {ALLERGEN_TYPES.map((option) => (
            <option key={option.id || "all"} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {error ? (
        <p data-testid={ALG.error} className="py-4 text-center text-sm text-rose-600">
          {error}
        </p>
      ) : loading ? (
        <div
          data-testid={ALG.loading}
          className="flex items-center justify-center gap-2 py-4 text-sm text-slate-500"
        >
          <Loader2 className="h-4 w-4 animate-spin" />
          Searching allergen catalog…
        </div>
      ) : !debounced ? (
        <p data-testid={ALG.hint} className="py-4 text-center text-sm text-slate-500">
          Type an allergen name or synonym to search the master list.
        </p>
      ) : !items.length ? (
        <p data-testid={ALG.empty} className="py-4 text-center text-sm text-slate-500">
          No allergens match “{debounced}”.
        </p>
      ) : (
        <ul
          data-testid={ALG.results}
          className="max-h-48 overflow-y-auto rounded-md border border-[#e4d8c8] bg-white"
        >
          {items.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                data-testid={`${ALG.result}-${item.id}`}
                onClick={() => selectItem(item)}
                disabled={readOnly}
                className="block w-full px-3 py-2 text-left text-sm text-slate-700 hover:bg-[#F3EEF8] hover:text-[#6B4C9A]"
              >
                {formatSearchAllergen(item)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
